import type { GraphDocument } from '../../entities/graph-document';
import type { JourneyNode, NodeType } from '../../entities/node';

export type OutlineSortKey = 'displayName' | 'type' | 'updatedAt' | 'connectionCount';
export type OutlineGroupBy = 'none' | 'type';

export interface OutlineRow {
	node: JourneyNode;
	connectionCount: number;
	relatedPreview: string;
	searchText: string;
}

export interface OutlineRowGroup {
	id: 'all' | NodeType;
	rows: OutlineRow[];
}

export function deriveOutlineRows(graph: Pick<GraphDocument, 'nodes' | 'connections'>): OutlineRow[] {
	const connectionCountByNode = graph.connections.reduce<Record<string, number>>((counts, connection) => {
		counts[connection.sourceNodeId] = (counts[connection.sourceNodeId] ?? 0) + 1;
		counts[connection.targetNodeId] = (counts[connection.targetNodeId] ?? 0) + 1;
		return counts;
	}, {});

	return graph.nodes.map((node) => {
		const relatedPreview = previewRelatedConnections(node.nodeId, graph.nodes, graph.connections);

		return {
			node,
			connectionCount: connectionCountByNode[node.nodeId] ?? 0,
			relatedPreview,
			searchText: [
				node.displayName,
				node.summary ?? '',
				node.tags.join(' '),
				relatedPreview,
			].join(' ').toLowerCase(),
		};
	});
}

export function filterAndSortOutlineRows(
	rows: OutlineRow[],
	options: {
		searchQuery: string;
		localVisibleTypes: NodeType[];
		sortKey: OutlineSortKey;
		sortDirection: 'asc' | 'desc';
	},
): OutlineRow[] {
	const normalizedQuery = options.searchQuery.trim().toLowerCase();

	return rows
		.filter((row) => {
			if (!options.localVisibleTypes.includes(row.node.type)) {
				return false;
			}

			if (!normalizedQuery) {
				return true;
			}

			return row.searchText.includes(normalizedQuery);
		})
		.sort((left, right) => compareOutlineRows(left, right, options.sortKey, options.sortDirection));
}

export function groupOutlineRows(rows: OutlineRow[], groupBy: OutlineGroupBy): OutlineRowGroup[] {
	if (groupBy === 'none') {
		return [{ id: 'all', rows }];
	}

	return (['Human', 'Project', 'Occupation'] as const)
		.map((type) => ({
			id: type,
			rows: rows.filter((row) => row.node.type === type),
		}))
		.filter((group) => group.rows.length > 0);
}

export function seedOutlineVisibleTypes(visibleType: NodeType | 'All'): NodeType[] {
	return visibleType === 'All' ? ['Human', 'Project', 'Occupation'] : [visibleType];
}

function compareOutlineRows(
	left: OutlineRow,
	right: OutlineRow,
	sortKey: OutlineSortKey,
	sortDirection: 'asc' | 'desc',
): number {
	const direction = sortDirection === 'asc' ? 1 : -1;
	const secondary = left.node.displayName.localeCompare(right.node.displayName);

	if (sortKey === 'connectionCount') {
		const primary = left.connectionCount - right.connectionCount;
		return primary === 0 ? secondary : direction * primary;
	}

	if (sortKey === 'updatedAt') {
		const primary = left.node.updatedAt.localeCompare(right.node.updatedAt);
		return primary === 0 ? secondary : direction * primary;
	}

	if (sortKey === 'type') {
		const primary = left.node.type.localeCompare(right.node.type);
		return primary === 0 ? secondary : direction * primary;
	}

	return direction * secondary;
}

function previewRelatedConnections(
	nodeId: string,
	nodes: JourneyNode[],
	connections: GraphDocument['connections'],
): string {
	const relatedNodeIds = connections
		.filter((connection) => connection.sourceNodeId === nodeId || connection.targetNodeId === nodeId)
		.slice(0, 3)
		.map((connection) => (connection.sourceNodeId === nodeId ? connection.targetNodeId : connection.sourceNodeId));

	const names = relatedNodeIds
		.map((relatedNodeId) => nodes.find((node) => node.nodeId === relatedNodeId)?.displayName)
		.filter((name): name is string => Boolean(name));

	return names.length > 0 ? names.join(' • ') : '—';
}